import { useState, useEffect, useCallback, useRef, type ReactNode, type FC } from 'react';
import { toast } from 'react-hot-toast';

import { AuthContext, type User } from './AuthContextValue';
import client, { setAuthToken, setAuthErrorHandler } from '../api/client';

export const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const authRequestVersionRef = useRef(0);

  const fetchSessionUser = useCallback(async (): Promise<User> => {
    const response = await client.get<User>('/auth/me');
    return response.data;
  }, []);

  const invalidateAuthRequests = useCallback(() => {
    authRequestVersionRef.current += 1;
  }, []);

  // Handle session expiry by clearing auth state and redirecting to login
  const handleAuthError = useCallback(() => {
    invalidateAuthRequests();
    localStorage.removeItem('charon_auth_token');
    setAuthToken(null);
    setUser(null);
    setIsLoading(false);
    toast.error('Session expired. Please log in again.', {
      id: 'auth-session-expired',
      duration: 10000,
    });
  }, [invalidateAuthRequests]);

  // Register auth error handler on mount; unregister on unmount so the axios
  // interceptor never calls a stale handler after the provider is gone
  useEffect(() => {
    setAuthErrorHandler(handleAuthError);
    return () => setAuthErrorHandler(null);
  }, [handleAuthError]);

  useEffect(() => {
    const checkAuth = async () => {
      const requestVersion = authRequestVersionRef.current + 1;
      authRequestVersionRef.current = requestVersion;

      try {
        const stored = localStorage.getItem('charon_auth_token');
        if (stored) {
          setAuthToken(stored);
        } else {
          // No token in localStorage - don't even try to authenticate
          // This prevents re-authentication via HttpOnly cookie after logout
          setAuthToken(null);
          if (authRequestVersionRef.current === requestVersion) {
            setUser(null);
            setIsLoading(false);
          }
          return;
        }
        const response = await fetchSessionUser();
        if (authRequestVersionRef.current === requestVersion) {
          setUser(response);
        }
      } catch {
        if (authRequestVersionRef.current === requestVersion) {
          setAuthToken(null);
          setUser(null);
        }
      } finally {
        if (authRequestVersionRef.current === requestVersion) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();
  }, [fetchSessionUser]);

  const login = useCallback(async (token?: string) => {
    const requestVersion = authRequestVersionRef.current + 1;
    authRequestVersionRef.current = requestVersion;
    setIsLoading(true);
    toast.dismiss('auth-session-expired');

    if (token) {
      localStorage.setItem('charon_auth_token', token);
      setAuthToken(token);
    }

    try {
      const response = await fetchSessionUser();
      if (authRequestVersionRef.current === requestVersion) {
        setUser(response);
      }
    } catch (error) {
      if (authRequestVersionRef.current === requestVersion) {
        setUser(null);
        setAuthToken(null);
        localStorage.removeItem('charon_auth_token');
      }
      throw error;
    } finally {
      if (authRequestVersionRef.current === requestVersion) {
        setIsLoading(false);
      }
    }
  }, [fetchSessionUser]);

  const logout = useCallback(async () => {
    invalidateAuthRequests();
    // Revoke the axios Authorization header immediately so no new API calls
    // are made with the old token while the backend invalidates the session.
    setAuthToken(null);

    try {
      // Invalidate the server-side session BEFORE clearing local state.
      // The browser still sends the auth_token cookie with this request,
      // so it authenticates correctly even without the Authorization header.
      // Doing this first closes the window where local state appears cleared
      // but the server-side session (and cookie) is still valid.
      await client.post('/auth/logout');
    } catch (error) {
      console.error("Logout failed", error);
    } finally {
      localStorage.removeItem('charon_auth_token');
      setUser(null);
      setIsLoading(false);
    }
  }, [invalidateAuthRequests]);

  // Re-fetches /auth/me and updates context state without touching the
  // token/session lifecycle. Used by settings mutations (e.g. the "What's
  // New" changelog opt-out toggle) that change a field on the user row and
  // need the UI to reflect it immediately. A failure here is non-critical —
  // it's swallowed rather than treated as session expiry, which is already
  // handled separately by the axios 401 interceptor (handleAuthError).
  const refetchUser = useCallback(async () => {
    try {
      const response = await fetchSessionUser();
      setUser(response);
    } catch {
      // Intentionally silent — see comment above.
    }
  }, [fetchSessionUser]);

  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await client.post('/auth/change-password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
    } catch (error: unknown) {
      // Extract error message from API response
      const message = error instanceof Error
        ? error.message
        : typeof error === 'object' && error !== null && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error || 'Password change failed'
          : 'Password change failed';
      throw new Error(message, {
        cause: error,
      });
    }
  };

  // Auto-logout logic
  useEffect(() => {
    if (!user) return;

    const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes
    let timeoutId: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('Auto-logging out due to inactivity');
        logout();
      }, TIMEOUT_MS);
    };

    // Initial timer start
    resetTimer();

    // Event listeners for activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handleActivity = () => resetTimer();

    for (const event of events) {
      window.addEventListener(event, handleActivity);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      for (const event of events) {
        window.removeEventListener(event, handleActivity);
      }
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, login, logout, changePassword, refetchUser, isAuthenticated: !!user, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
